<template>
  <div id="yearly-chart">
    <!-- Chart -->
    <apexchart
      @click="onBarClick"
      type="bar"
      height="auto"
      :options="chartOptions"
      :series="series"
    />

    <!-- Drilldown Modal -->
    <div
      class="modal fade"
      id="yearlyModal"
      tabindex="-1"
      aria-labelledby="yearlyModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-xxl modal-dialog-centered" style="max-width: 80vw;">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="yearlyModalLabel">
              {{ currentEvent ? currentEvent.event : '' }}
            </h5>
            <button type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <!-- Pagination + Filters -->
            <div class="row align-item-center">
              <div class="col-auto pb-2 mr-2">
                <nav aria-label="...">
                  <ul class="pagination text-smallest mb-0 pb-0">
                    <li class="page-item"
                        :class="{ 'disabled': loading && !findingByPortsFinding.length || pagination.currentPage === 1 }">
                      <button class="page-link" @click="setPage(1,year,office)">First</button>
                    </li>
                    <li v-for="page in paginationPages"
                        :key="page"
                        class="page-item"
                        :class="{ 'disabled': page === '...', 'active': page === pagination.currentPage }">
                      <button class="page-link" @click="setPage(page,year,office)">{{ page }}</button>
                    </li>
                    <li class="page-item" :class="{ 'disabled': pagination.currentPage === pagination.lastPage }">
                      <button class="page-link" @click="setPage(pagination.lastPage,year,office)">Last</button>
                    </li>
                  </ul>
                </nav>
              </div>
              <div class="col-auto">
                <select
                  :disabled="loading && !findingByPortsFinding.length"
                  v-model="year"
                  class="form-control form-control-sm">
                  <option :value="null">{{ Math.min(...years) }}-{{ Math.max(...years) }}</option>
                  <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
                </select>
              </div>
              <div class="col-auto" v-if="['k-line-persons', 'k-line-persons2'].includes(user.roles_permissions?.roles[0]) === false">
                <button
                  class="btn btn-dark btn-sm force-white-all"
                  :disabled="!findingByPortsFinding.length"
                  @click.prevent="generateCsv">
                  <font-awesome-icon icon="download" class="mr-2" />
                  DOWNLOAD CSV
                </button>
              </div>
            </div>

            <!-- Drilldown Table -->
            <table class="small e-tbl">
              <thead class="bg-white" style="position: sticky; top: 0; z-index: 2">
              <tr>
                <th class="align-middle text-center">#</th>
                <th class="align-middle text-center">VESSEL</th>
                <th class="align-middle text-center">MANAGER</th>
                <th class="align-middle text-center">REPORT TITLE</th>
                <th class="align-middle text-center">COMMENCEMENT DATE</th>
                <th class="align-middle text-center">CODE</th>
                <th class="align-middle text-center">TYPE OF FINDING</th>
                <th class="align-middle text-center">RISQ FINDING</th>
                <th class="align-middle text-center">REVISION NUMBER</th>
                <th class="align-middle text-center">ACTION</th>
              </tr>
              </thead>
              <tbody>
              <tr v-if="loading && !findingByPortsFinding.length">
                <td colspan="100%"><app-loading /></td>
              </tr>
              <tr v-else-if="!loading && findingByPortsFinding.length"
                  v-for="(evt,index) in findingByPortsFinding"
                  :key="evt.rs_id">
                <td class="align-middle text-center">{{ index + 1 }}</td>
                <td class="align-middle text-center">{{ evt.vessel_name }}</td>
                <td class="align-middle text-center">{{ evt.management_office_name }}</td>
                <td class="align-middle text-center">{{ evt.rs_title }}</td>
                <td class="align-middle text-center">{{ evt.inspection_commencement_date }}</td>
                <td class="align-middle text-center">{{ evt.question_code }}</td>
                <td class="align-middle text-center">{{ evt.type_of_finding }}</td>
                <td class="align-middle text-center">{{ evt.main_question }}</td>
                <td class="align-middle text-center">{{ evt.revision_number }}</td>
                <td class="align-middle text-center">
                  <button class="e-btn e-btn-blue e-btn-sm e-text-white my-1"
                          @click="showReport(evt.rs_id)">
                    <font-awesome-icon icon="eye" /> View
                  </button>
                </td>
              </tr>
              <tr v-else>
                <td colspan="100%" class="align-middle text-center">
                  <p class="h5 py-1">NO RIGHTSHIP FOUND</p>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { PaginationMixin } from "@/mixins/PaginationMixin";
import appLoading from "@/components/elements/AppLoading.vue";
import { ExcelService } from "@/services/ExcelService";
import {permissionMixin} from "@/mixins/permissionMixins";

export default {
  name: "RsFindingsByPortYearChart",
  props: {
    rawData: { // raw API response: [{eventOneId, event, years:{2020:0,...}}, ...]
      type: Array,
      required: true
    },
    office: {
      type: [String, Number],
      default: null
    },
    port:{
      type: [String, Number],
      default: null
    }
  },
  mixins: [PaginationMixin, permissionMixin],
  components: { appLoading },
  data() {
    return {
      year: null,
      loading: false,
      currentEvent: null,
      chartOptions: {
        chart: { type: "bar" },
        colors: ["#bfbfbf", "#a6a6a6", "#ffd966", "#f7caac", "#f4b083", "#ff0000"],
        plotOptions: {
          bar: { horizontal: true, dataLabels: { position: "top" } }
        },
        dataLabels: { enabled: false },
        stroke: { show: true, colors: ["#000"] },
        tooltip: { followCursor: true, shared: true, intersect: false },
        fill: {
          colors: ["#bfbfbf", "#a6a6a6", "#ffd966", "#f7caac", "#f4b083", "#ff0000"]
        },
        grid: {
          borderColor: "#e9e9e9",
          position: "front",
          xaxis: { lines: { show: true } }
        },
        xaxis: { categories: [] }
      },
      series: [],
      categories: [],
      years: [],
      categoryObject: []
    };
  },
  created() {
    this.transformData(this.rawData);
  },
  methods: {
    ...mapActions(["getFindingByPortsFinding"]),
    transformData(data) {
      this.categoryObject = data;
      this.categories = data.map(d => d.event);
      this.years = Object.keys(data[0].years);

      this.series = this.years.map(y => ({
        name: y,
        data: data.map(d => d.years[y])
      }));

      this.chartOptions.xaxis.categories = this.categories;
    },
    async onBarClick(evt) {
      const isDownloadMenuItem = $(evt.target).hasClass("apexcharts-menu-item");
      if (isDownloadMenuItem) return;

      const innerText = evt.target.innerHTML;
      const code = innerText.split(")")[0].trim();

      this.currentEvent = this.categoryObject.find(evt2 => {
        const eventCode = evt2.event.split(")")[0].trim();
        return eventCode === code;
      });

      if (this.currentEvent) {
        this.$store.commit("setFindingByPortsFinding", []);
        $("#yearlyModal").modal("show");
        this.loading = true;
        this.year = null;
        await this.setPage(null, null, this.office);
        this.loading = false;
      }
    },
    async setPage(page = null, year = null, office = null) {
      if (!this.currentEvent) return;

      this.$store.commit("setFindingByPortsFinding", []);
      this.loading = true;

      const params = {
        finding: this.currentEvent?.eventOneId,
        page: page ?? 1,
        year: year ?? null,
        office: office ?? null,
        port_id: this.port ?? null,
      };

      await this.getFindingByPortsFinding(params);
      this.loading = false;
    },
    async generateCsv() {
      swal.fire({
        title: "GENERATING CSV FILE",
        allowEscapeKey: false,
        allowEnterKey: false,
        allowOutsideClick: false
      });
      swal.showLoading();
      await ExcelService.csv("RIGHTSHIP_EVENT_FINDINGS_BY_PORTS", {
        finding: this.currentEvent?.eventOneId ?? null,
        year: this.year ?? null,
        office: this.office,
        port_id: this.port,
      });
      swal.close();
    },
    async showReport(reportId) {
      const route = this.$router.resolve({
        name: "RsMain",
        params: { id: reportId }
      });
      window.open(route.href, "_blank");
    }
  },
  computed: {
    ...mapGetters(["findingByPortsFinding", "pagination"])
  },
  watch: {
    year(val) {
      this.setPage(null, val, this.office);
    }
  }
};
</script>

<style scoped>
table tr td {
  font-size: 14px !important;
}
</style>
